import React, { Component } from 'react';
import PatientArticleData from 'model/PatientArticleData';
import DocumentNavigation from 'components/DocumentNavigation';
import styles from 'containers/Layouts/Layout.module.css';
import ContentService from 'services/ContentService';
import PatientArticleService from 'services/PatientArticleService';
import { trackPromise } from 'react-promise-tracker';
import Back2Top from './Back2Top';
import i18n from 'i18n/I18nInstance';
import AnalyticsService from 'services/AnalyticsService';
import { ContentType, EventType } from '../model/enums';
import PdfViewer from './PdfViewer';

type IPropsPatientArticle = {
    mediaKey: string;
    disablePrintMobileOnly: boolean;
};

type IStatePatientArticle = {
    data: PatientArticleData | null;
    error: string | null;
    mediaKey: string | null;
    title: string | null;
};

export default class PatientArticle extends Component<IPropsPatientArticle, IStatePatientArticle> {
    state: IStatePatientArticle = {
        data: null,
        error: null,
        mediaKey: null,
        title: null,
    };

    async componentDidMount() {
        const mediaKey = decodeURIComponent(this.props.mediaKey);
        try {
            let patientArticleData = (await trackPromise(
                ContentService.getPatientArticle(mediaKey),
            )) as PatientArticleData;
            this.setState({
                data: patientArticleData,
                mediaKey: mediaKey,
                title: patientArticleData.title!,
            });
        } catch (error) {
            this.setState({ error: error.message });
        }
    }

    onDownloadButtonClicked = async (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        await trackPromise(
            ContentService.downloadPatientArticlePdf(this.state.mediaKey!, this.state.title!),
        );
        event.stopPropagation();
        AnalyticsService.recordEventType(this.state.data, EventType.Download, ContentType.Document);
    };

    onPrintButtonClicked = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        event.stopPropagation();
        window.print();
        AnalyticsService.recordEventType(this.state.data, EventType.Print, ContentType.Document);
    };

    render() {
        const i18n_resources = i18n.translator.formatMessage({
            id: 'common.Resources',
            defaultMessage: 'RESOURCES',
        });

        if (this.state.error) {
            return <div>{this.state.error}</div>;
        } else if (this.state.data === null) {
            return <div></div>;
        }

        const isMobile = () => {
            const UA = navigator.userAgent;
            // Test for Mobi in the user agent. Recommended by mozilla, here:
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
            return /Mobi/g.test(UA);
        };

        const disablePrint = isMobile() && this.props.disablePrintMobileOnly;

        /*
            We use dangerouslySetInnerHTML to set the inner html due to the fact
            we trust the html document that comes from the StayWell UCR.
            After we retrieve the document we then add a style css block to style the raw html.
        */
        const rawHtmlDocument = this.state.data.document as string;
        const htmlDocumentWithStyles =
            PatientArticleService.addPatientArticleStyleToHtml(rawHtmlDocument);

        return (
            <>
                <div className={styles.educationRoot} data-qaid="patientArticleRoot">
                    <div className={styles.educationContainer} data-qaid="patientArticle">
                        <DocumentNavigation
                            goBackText={i18n_resources}
                            actionsButtonsDownloadHandler={this.onDownloadButtonClicked}
                            actionsButtonsPrintHandler={this.onPrintButtonClicked}
                            showActionButtons={this.state.data.pdfUrl === '' && !disablePrint}
                            documentTitle={this.state.title}
                        ></DocumentNavigation>
                        {this.state.data.pdfUrl ? (
                            <div className={styles.educationText}>
                                <PdfViewer
                                    licenseKey={this.state.data.pdfJsExpressKey!}
                                    docUrl={this.state.data.pdfUrl!}
                                    analyticsData={this.state.data}
                                ></PdfViewer>
                            </div>
                        ) : (
                            <div
                                dangerouslySetInnerHTML={{ __html: htmlDocumentWithStyles }}
                                className={styles.educationText}
                                id="patientArticleContent"
                                data-qaid="patientArticleContent"
                            />
                        )}
                        <DocumentNavigation
                            goBackText={i18n_resources}
                            showActionButtons={false}
                        ></DocumentNavigation>
                        <Back2Top />
                    </div>
                </div>
            </>
        );
    }
}
